"use client";

import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import Image from "next/image";

export default function Preloader() {
  const containerRef = useRef<HTMLDivElement>(null);
  const logoPillRef = useRef<HTMLDivElement>(null);
  const logoImageRef = useRef<HTMLDivElement>(null);
  const subtitleContainerRef = useRef<HTMLDivElement>(null);
  const leftLineRef = useRef<HTMLDivElement>(null);
  const rightLineRef = useRef<HTMLDivElement>(null);
  const sweepRef = useRef<HTMLDivElement>(null);
  const scannerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const container = containerRef.current;
    const logoPill = logoPillRef.current;
    const logoImage = logoImageRef.current;
    const subtitle = subtitleContainerRef.current;
    const leftLine = leftLineRef.current;
    const rightLine = rightLineRef.current;
    const sweep = sweepRef.current;
    const scanner = scannerRef.current;

    if (!container || !logoPill || !logoImage || !subtitle || !leftLine || !rightLine || !sweep || !scanner) {
      setVisible(false);
      return;
    }

    document.documentElement.classList.add("loading-lock");
    document.body.classList.add("loading-lock");

    // Dynamic sizing based on screen width
    const isMobile = window.innerWidth < 768;
    const startWidth = isMobile ? 140 : 180;
    const targetWidth = isMobile ? 340 : 500;

    const tl = gsap.timeline({
      onComplete: () => {
        // Wait a tiny bit before removing the lock so the DOM unmounts first
        setTimeout(() => {
          document.documentElement.classList.remove("loading-lock");
          document.body.classList.remove("loading-lock");
        }, 50);
        setVisible(false);
      }
    });

    // Scanner loop (runs independently)
    const scannerTl = gsap.timeline({ repeat: -1, yoyo: true });
    scannerTl.to(scanner, { x: "300%", duration: 1.5, ease: "sine.inOut" });

    // Initial States
    gsap.set(logoPill, { width: startWidth, opacity: 0, scale: 0.4, y: 30 });
    gsap.set(logoImage, { opacity: 0, scale: 0.8, filter: "blur(5px)" });
    gsap.set(subtitle, { opacity: 0, y: 15 });
    gsap.set([leftLine, rightLine], { scaleX: 0, transformOrigin: "center" });
    gsap.set(sweep, { x: "-200%" });

    // Timeline Animation Sequence - Ultra Smooth Entrance
    // 1. Pill smoothly rises and fades in
    tl.to(logoPill, {
      y: 0,
      opacity: 1,
      scale: 1,
      duration: 1.2,
      ease: "expo.out"
    })
      // 2. Seamlessly expand into a wide pill (heavily overlapping)
      .to(logoPill, {
        width: targetWidth,
        duration: 1.4,
        ease: "power4.inOut"
      }, "-=0.8")
      // 3. Logo comes into crystal clear focus as the pill expands
      .to(logoImage, {
        opacity: 1,
        scale: 1,
        filter: "blur(0px)",
        duration: 1.2,
        ease: "expo.out"
      }, "-=1.0")
      // 4. Subtitle glides up elegantly
      .to(subtitle, {
        opacity: 1,
        y: 0,
        duration: 1.0,
        ease: "power3.out"
      }, "-=0.8")
      // 5. Lines smoothly extend outward
      .to([leftLine, rightLine], {
        scaleX: 1,
        duration: 1.2,
        ease: "expo.out"
      }, "-=0.8")
      // 6. Polished glass sweep slides across
      .to(sweep, {
        x: "300%",
        duration: 1.8,
        ease: "power2.inOut"
      }, "-=0.8")
      // 7. Very gentle floating hold state
      .to(logoPill, {
        y: -4,
        duration: 1.2,
        yoyo: true,
        repeat: 1,
        ease: "sine.inOut"
      }, "-=0.6")

      // 8. Creative Exit Sequence
      // Subtitle elements retract cleanly
      .to([leftLine, rightLine], {
        scaleX: 0,
        duration: 0.4,
        ease: "power3.in"
      })
      .to(subtitle, {
        opacity: 0,
        y: 15,
        duration: 0.4,
        ease: "power3.in"
      }, "<0.1")
      .to(scanner, {
        opacity: 0,
        duration: 0.2
      }, "<")

      // The pill gracefully compresses back to a circle
      .to(logoPill, {
        width: startWidth,
        duration: 0.8,
        ease: "power4.inOut"
      }, "+=0.1")
      // While the pill compresses, the logo inside dynamically scales down and fades
      .to(logoImage, {
        scale: 0.3,
        opacity: 0,
        duration: 0.7,
        ease: "power3.inOut"
      }, "<0.1")
      // The remaining circle elegantly pops away
      .to(logoPill, {
        scale: 0.95,
        opacity: 0,
        duration: 0.5,
        ease: "back.in(2)"
      }, "-=0.2")
      // The main background curtain lifts smoothly
      .to(container, {
        y: "-100%",
        borderBottomLeftRadius: "60%",
        borderBottomRightRadius: "60%",
        duration: 1.2,
        ease: "power4.inOut"
      }, "-=0.2");

    return () => {
      tl.kill();
      scannerTl.kill();
      document.documentElement.classList.remove("loading-lock");
      document.body.classList.remove("loading-lock");
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 w-full h-full bg-[#030712] z-[99999] flex flex-col items-center justify-center select-none overflow-hidden origin-top"
    >
      {/* Professional Tech/Consultancy Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(13,148,136,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(13,148,136,0.03)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none" />

      {/* Central Ambient Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] max-w-[800px] h-[80vw] max-h-[800px] bg-teal-500/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="flex flex-col items-center justify-center gap-10 max-w-lg w-full relative z-10 px-4">

        {/* Expanding Pill Logo Container */}
        <div
          ref={logoPillRef}
          className="relative h-[140px] md:h-[180px] bg-gradient-to-tr from-white via-white to-teal-50/60 rounded-[100px] shadow-[0_0_80px_rgba(255,255,255,0.1),_inset_0_0_30px_rgba(13,148,136,0.08)] flex items-center justify-center overflow-hidden border border-teal-100/40 will-change-transform"
        >
          {/* Animated Glass Shine Reflection */}
          <div
            ref={sweepRef}
            className="absolute top-0 left-0 w-[50%] h-full bg-gradient-to-r from-transparent via-teal-100/60 to-transparent skew-x-[-25deg] pointer-events-none z-20"
          />

          <div ref={logoImageRef} className="relative w-[92%] h-[75%] flex items-center justify-center z-10">
            <Image
              src="/originalLogoTransparent.png"
              alt="Compare All Logo"
              fill
              className="object-contain"
              priority
              quality={85}
              sizes="(max-width: 768px) 240px, 300px"
            />
          </div>
        </div>

        {/* Unique Subtitle Layout */}
        <div ref={subtitleContainerRef} className="flex flex-col items-center gap-4 w-full">
          <div className="flex items-center gap-4 md:gap-6 w-full justify-center">
            {/* Left Accent Line */}
            <div ref={leftLineRef} className="h-[1px] w-12 md:w-24 bg-gradient-to-r from-transparent to-teal-500/60" />

            <p className="text-[10px] md:text-[13px] font-sans font-bold uppercase tracking-[0.45em] text-transparent bg-clip-text bg-gradient-to-r from-teal-300 via-white to-teal-300 text-center drop-shadow-sm whitespace-nowrap">
              Don't just renew, have a review
            </p>

            {/* Right Accent Line */}
            <div ref={rightLineRef} className="h-[1px] w-12 md:w-24 bg-gradient-to-l from-transparent to-teal-500/60" />
          </div>

          {/* Animated Tech Scanner Line */}
          <div className="w-[150px] md:w-[200px] h-[1px] bg-white/5 relative overflow-hidden rounded-full">
            <div ref={scannerRef} className="absolute top-0 left-0 w-1/4 h-full bg-teal-400 blur-[0.5px] shadow-[0_0_5px_#2dd4bf]" />
          </div>
        </div>

      </div>
    </div>
  );
}
